/**
 * Local observation state of one live transport session's connection phases: a stable [epoch] naming this session
 * instance, plus monotonic marks for dial start, first channel-open (handshake), and first inbound sync-protocol
 * line. A status surface renders these so an agent can tell "handshaked since I last looked" apart from "open since
 * long before my turn" — the distinction whose absence made accession latency structurally unobservable.
 *
 * Layer adjudication (link-native modeling): transport-layer **local observation** — never persisted, never
 * replicated, never a wire message. No existing primitive expresses it: the graph deliberately carries no
 * runtime-clock facts (time-dependent standing is a field reading), and this is a single-observer view of the
 * node's own sockets, same family as the honest "peer links open right now" count.
 *
 * Durations derive only within one instance (= one [epoch]): the same epoch across two reads is the same live
 * session; a new epoch means a reconnect or a process restart, so cross-epoch arithmetic is meaningless by design.
 * Epochs embed a per-process boot token because session ids restart at 1 in a new process, and a restarted node
 * must not impersonate the continuity of the one it replaced.
 */
package com.oasisfeng.invaluable.p2p.core

import kotlin.random.Random
import kotlin.time.ComparableTimeMark
import kotlin.time.Duration
import kotlin.time.TimeSource

class SessionTimeline(val epoch: String, private val timeSource: TimeSource.WithComparableMarks = TimeSource.Monotonic) {

    val started: ComparableTimeMark = timeSource.markNow()
    var handshaked: ComparableTimeMark? = null
        private set
    var firstSync: ComparableTimeMark? = null
        private set

    /** First channel-open wins — a re-fired open event must not rewrite when the handshake was first observed. */
    fun markHandshaked() { if (handshaked == null) handshaked = timeSource.markNow() }

    /** First inbound protocol line wins. Transport-level: "sync traffic began", not "sync completed". */
    fun markFirstSync() { if (firstSync == null) firstSync = timeSource.markNow() }

    /** Dial-to-open duration; null until the handshake is observed. Only meaningful within this epoch. */
    val timeToReady: Duration? get() = handshaked?.minus(started)

    /**
     * One-line render of the phase marks. [preexistingBefore] is the caller's previous status-read mark: a session
     * whose handshake is at or before it is labeled `preexisting` — its handshake must not be read as having
     * happened since that previous read, let alone "this turn".
     */
    fun describe(preexistingBefore: ComparableTimeMark? = null): String = buildString {
        append("epoch=").append(epoch)
        append(" started ").append(started.elapsedNow().inWholeSeconds).append("s ago")
        val hs = handshaked
        append(" handshaked ").append(if (hs == null) "null" else "+${(hs - started).inWholeMilliseconds}ms")
        val fs = firstSync
        append(" first_sync ").append(if (fs == null) "null" else "+${(fs - started).inWholeMilliseconds}ms")
        if (hs != null) append(" ready=${(hs - started).inWholeMilliseconds}ms")
        if (hs != null && preexistingBefore != null && hs <= preexistingBefore) append(" preexisting")
    }

    companion object {
        /** Regenerated each process start; distinguishes this boot's epochs from a previous incarnation's. */
        val bootToken: String = buildString { repeat(4) { append("0123456789abcdefghijklmnopqrstuvwxyz"[Random.nextInt(36)]) } }

        /** Canonical epoch id: transport kind + per-process session id + boot token, e.g. `rtc3@k9f2`. */
        fun epochOf(kind: String, id: Int): String = "$kind$id@$bootToken"
    }
}
